import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { formatInTimeZone } from 'date-fns-tz';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getAppAccessSettings } from '../services/supabase/appAccessSettings.service';
import { isWithinAccessWindow, type AppAccessSettingsRow } from '../utils/appAccessWindow';

interface AccessWindowGateProps {
  userRole: string | undefined | null;
  children: React.ReactNode;
}

export function AccessWindowGate({ userRole, children }: AccessWindowGateProps) {
  const { signOut } = useAuth();
  const [settings, setSettings] = useState<AppAccessSettingsRow | null | undefined>(undefined);

  const load = useCallback(async () => {
    const row = await getAppAccessSettings();
    setSettings(row ?? null);
  }, []);

  useEffect(() => {
    void load();
    const id = window.setInterval(() => void load(), 60_000);
    return () => window.clearInterval(id);
  }, [load]);

  useEffect(() => {
    const channel = supabase
      .channel('app-access-settings')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'app_access_settings' },
        () => void load()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [load]);

  if (settings === undefined) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (userRole === 'admin') {
    return <>{children}</>;
  }

  if (!settings || !settings.is_enabled) {
    return <>{children}</>;
  }

  const now = new Date();
  if (isWithinAccessWindow(now, settings)) {
    return <>{children}</>;
  }

  const tz = settings.time_zone || 'Asia/Manila';
  const localNow = formatInTimeZone(now, tz, 'EEEE, MMM d, yyyy — HH:mm');
  const startLabel = settings.window_start?.slice(0, 5) ?? '';
  const endLabel = settings.window_end?.slice(0, 5) ?? '';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        bgcolor: 'background.default',
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, textAlign: 'center' }}>
        Outside office hours
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 0.5, textAlign: 'center', maxWidth: 420 }}>
        This app is only available during the scheduled window ({startLabel} – {endLabel},{' '}
        <Box component="span" sx={{ fontWeight: 700 }}>
          {tz}
        </Box>
        ). The end time is exclusive (access stops at {endLabel}).
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        Your current time in that zone: {localNow}
      </Typography>
      <Button variant="contained" onClick={() => void signOut()} sx={{ textTransform: 'none' }}>
        Sign out
      </Button>
    </Box>
  );
}
